import * as React from 'react'
import { Button } from '../../components/ui/Button';
import { FontAwesomeIcon } from '../../components/ui/FontAwesomeIcon';
import { Modal, ModalContent, ModalFooter } from '../../components/ui/Modal';
import { HttpRequest } from '../../util/http';
import { Container } from '../Container';

interface ISegmentContainerState {
  showModal?: boolean
  showDeleteModal: boolean
  segments?: ISegment[]
  parentOptions?: IOption[]
  deleteSegmentId?: number
  currentSegment?: {
    id?: number
    segmentName?: string
    segmentAvid?: string
    segmentValueName?: string
    segmentValudAvid?: string
    fileColumnName?: string
    fileValue?: string
    operator?: string
    sortOrder?: number
    useExisting?: boolean
  }
}

interface IOption {
  key?: string
  value?: string
}

export interface ISegment {
  id?: number
  segmentName?: string
  segmentAvid?: string
  segmentValueName?: string
  segmentValudAvid?: string
  fileColumnName?: string
  fileValue?: string
  operator?: string
  sortOrder?: number
  useExisting?: boolean
}

export class SegmentsContainer extends Container<{}, ISegmentContainerState> {
  state: {
    showModal: boolean,
    showDeleteModal: boolean,
    segments: ISegment[],
    parentOptions: IOption[],
    deleteSegmentId: null,
    currentSegment: ISegment
  }

  constructor(props) {
    super(props)
    this.loadSegments()
    this.toggleUseExisting = this.toggleUseExisting.bind(this)
    this.state = {
      showModal: false,
      showDeleteModal: false,
      segments: [],
      parentOptions: [],
      deleteSegmentId: null,
      currentSegment: {
        id: 0,
        segmentName: '',
        segmentAvid: '',
        segmentValueName: '',
        segmentValudAvid: '',
        fileColumnName: '',
        fileValue: '',
        operator: '',
        sortOrder: 0,
        useExisting: true
      },
    }
  }

  loadSegments() {
    HttpRequest.fetch<ISegment[]>('scripts/segments/get_all', {})
      .then((segments) => {
        const options = []
        options.push({ key: '', value: '-- Select an existing segment --' })
        const avids = [...new Set(segments.map((item) => item.segmentAvid))];
        for (const avid of avids) {
          const avidSegments = segments.find((segment) => segment.segmentAvid === avid)
          const avidLabel = avidSegments.segmentName
          options.push({ key: avid, value: avidLabel })
        }
        this.setState({ parentOptions: options, segments })
      });
  }

  toggleShowAddModal() {
    const { showModal } = this.state
    let { currentSegment } = this.state

    if (!showModal) {
      currentSegment = {
        id: 0,
        segmentName: '',
        segmentAvid: '',
        segmentValueName: '',
        segmentValudAvid: '',
        fileColumnName: '',
        fileValue: '',
        operator: '',
        sortOrder: 0,
        useExisting: true
      }
    }

    this.setState({ showModal: !showModal, currentSegment })
  }

  toggleShowEditModal(id) {
    const { showModal, segments } = this.state

    const currentSegment = segments.find((s) => s.id === id)
    if (!showModal) {
      this.setState({ showModal: !showModal, currentSegment })
    } else {
      this.setState({ showModal: !showModal })
    }
  }

  toggleShowDeleteModal(id) {
    const { showDeleteModal } = this.state
    let deleteSegmentId = null
    if (id) {
      deleteSegmentId = id
    }

    this.setState({ showDeleteModal: !showDeleteModal, deleteSegmentId })
  }

  toggleUseExisting() {
    const { currentSegment } = this.state
    const toggleExisting = !currentSegment.useExisting

    currentSegment.useExisting = toggleExisting

    this.setState({ currentSegment })
  }

  cancelSave() {
    const currentSegment = {
      id: null,
      segmentName: '',
      segmentAvid: '',
      segmentValueName: '',
      segmentValudAvid: '',
      fileColumnName: '',
      fileValue: '',
      operator: '',
      sortOrder: null
    }

    this.setState({ showModal: false, currentSegment })
  }

  updateSegmentLabel(segmentName) {
    const { currentSegment } = this.state
    currentSegment.segmentName = segmentName

    this.setState({ currentSegment })
  }

  updateSegmentValueName(segmentValueName) {
    const { currentSegment } = this.state
    currentSegment.segmentValueName = segmentValueName

    this.setState({ currentSegment })
  }

  updateFileColumn(fileColumnName) {
    const { currentSegment } = this.state
    currentSegment.fileColumnName = fileColumnName

    this.setState({ currentSegment })
  }

  updateFileValue(fileValue) {
    const { currentSegment } = this.state
    currentSegment.fileValue = fileValue

    this.setState({ currentSegment })
  }

  updateSortOrder(sortOrder) {
    const { currentSegment } = this.state
    currentSegment.sortOrder = sortOrder

    this.setState({ currentSegment })
  }

  updateSegmentParent(avid, label) {
    const { currentSegment } = this.state
    const name = label.selectedOptions[0].text

    currentSegment.segmentAvid = avid
    currentSegment.segmentName = name

    this.setState({ currentSegment })
  }

  deleteSegment(id) {
    const method = 'POST'
    const params = { id }
    const body = JSON.stringify(id)

    HttpRequest.fetch<ISegment[]>('scripts/segments/delete', { body, method, params })
      .then((segments) => {
        this.setState({ showDeleteModal: false, segments })
      })
  }

  saveSegment(evt) {
    evt.preventDefault();
    const { currentSegment } = this.state
    const method = 'POST'
    const params = currentSegment
    const body = JSON.stringify(currentSegment)

    HttpRequest.fetch<ISegment[]>('scripts/segments/save', { body, method, params })
      .then((segments) => {
        this.setState({ showModal: false, segments })
        this.loadSegments()
      })
  }

  render() {
    const { segments, showDeleteModal, showModal, deleteSegmentId, currentSegment } = this.state
    return (
      <div className="container">
        <section>
          <h3>Manage Segments</h3>
        </section>
        <section>
          <a className="btn btn-primary" onClick={() => this.toggleShowAddModal()}>Add Segment</a>
        </section>
        <section>
          <table>
            <thead>
              <tr>
                <th>Parent Label</th>
                <th>Segment Label</th>
                <th>File Column</th>
                <th>File Value</th>
                <th>Sort Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {segments.map((segment) =>
                <tr key={segment.id}>
                  <td>{segment.segmentName}</td>
                  <td>{segment.segmentValueName}</td>
                  <td>{segment.fileColumnName}</td>
                  <td>{segment.fileValue}</td>
                  <td>{segment.sortOrder}</td>
                  <td>
                    <Button onClick={() => this.toggleShowEditModal(segment.id)}>
                      <FontAwesomeIcon icon="edit" color="black" fixedWidth />
                    </Button>
                    <Button onClick={() => this.toggleShowDeleteModal(segment.id)}>
                      <FontAwesomeIcon icon="trash-alt" color="black" fixedWidth />
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
        <Modal open={showModal}>
          <form action="#">
            <ModalContent>
              <header>
                <h4>Save Segment</h4>
              </header>
              <section>
                <div className="row">
                  <div className="col s12 m6">
                    <fieldset>
                      <legend>Parent Segment</legend>
                      <div>
                        <input name="group1" type="radio" id="existing" onChange={this.toggleUseExisting} checked={currentSegment.useExisting} />
                        <label htmlFor="existing">Use Existing Segment</label>
                      </div>
                      <div>
                        <input name="group1" type="radio" id="new" onChange={this.toggleUseExisting} checked={!currentSegment.useExisting} />
                        <label htmlFor="new">Create New Segment</label>
                      </div>
                    </fieldset>
                    <div className="input-field">
                      <div className="label">Current Parent Segments</div>
                      <select className="browser-default" disabled={!currentSegment.useExisting}
                        value={this.state.currentSegment.segmentAvid}
                        onChange={(e) => this.updateSegmentParent(e.target.value, e.target)}>
                        {this.state.parentOptions.map((option) =>
                          <option key={option.key} value={option.key}>{option.value}</option>
                        )}
                      </select>
                    </div>
                    <div className="input-field">
                      <div className="label">New Segment Parent</div>
                      <input className="browser-default" readOnly={currentSegment.useExisting} required
                        value={this.state.currentSegment.segmentName}
                        onChange={(e) => this.updateSegmentLabel(e.target.value)}
                        type="text" />
                    </div>
                    <div className="input-field">
                      <div className="label">New Child Segment Label</div>
                      <input className="browser-default" required
                        value={this.state.currentSegment.segmentValueName}
                        onChange={(e) => this.updateSegmentValueName(e.target.value)}
                        type="text" />
                    </div>
                  </div>
                  <div className="col s12 m6">
                    <div className="input-field">
                      <div className="label">File Column Name</div>
                      <input className="browser-default" required
                        value={this.state.currentSegment.fileColumnName}
                        onChange={(e) => this.updateFileColumn(e.target.value)}
                        type="text" />
                    </div>
                    <div className="input-field">
                      <div className="label">File Value</div>
                      <input className="browser-default" required
                        value={this.state.currentSegment.fileValue}
                        onChange={(e) => this.updateFileValue(e.target.value)}
                        type="text" />
                    </div>
                    <div className="input-field">
                      <div className="label">Sort Order</div>
                      <input className="browser-default" required
                        value={this.state.currentSegment.sortOrder}
                        onChange={(e) => this.updateSortOrder(e.target.value)}
                        type="number" />
                    </div>
                  </div>
                </div>
              </section>
            </ModalContent>
            <ModalFooter>
              <Button type="submit" color="danger" onClick={(e) => this.saveSegment(e)}>
                <span>Save</span>
              </Button>
              <Button type="button" color="white" onClick={() => this.cancelSave()}>
                <span>Cancel</span>
              </Button>
            </ModalFooter>
          </form>
        </Modal>
        <Modal open={showDeleteModal}>
          <ModalContent>
            <header>
              <h4>Delete Segment?</h4>
            </header>
            <section>
              Are you sure you want to delete this segment?
            </section>
          </ModalContent>
          <ModalFooter>
            <Button type="button" color="danger" onClick={() => this.deleteSegment(deleteSegmentId)}>
              <span>Confirm</span>
            </Button>
            <Button type="button" color="white" onClick={() => this.toggleShowDeleteModal(null)}>
              <span>Cancel</span>
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    )
  }
}
